'use client'

import {
  hashKey,
  type QueryKey,
  useQueries,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback, useState } from 'react'

export interface LeaderboardPageData<TRow> {
  rows: TRow[]
  total: number
}

/** A run of consecutive loaded pages — one continuous range of ranks. */
export interface LeaderboardBlock<TRow> {
  firstPage: number
  lastPage: number
  rows: TRow[]
}

export interface LeaderboardSelfRow<TRow> {
  row: TRow
  /** The row is inside a loaded block (not only on its own page). */
  inList: boolean
}

interface UsePagedLeaderboardOptions<TRow> {
  /** Board identity: endpoint and filters. A new key starts from page 0. */
  queryKey: QueryKey
  fetchPage: (
    page: number,
    pageSize: number
  ) => Promise<LeaderboardPageData<TRow>>
  pageSize?: number
  enabled?: boolean
  /** The viewer's rank on this board, when known. */
  selfRank?: number
  isSelfRow?: (row: TRow) => boolean
}

const DEFAULT_PAGE_SIZE = 20
const STALE_TIME = 30_000
const INITIAL_PAGES = [0]

/** Pages that cover ranks `rank - radius … rank + radius`. */
export function pagesAroundRank(
  rank: number,
  radius: number,
  pageSize: number,
  totalPages: number
): number[] {
  const first = Math.max(0, Math.floor((rank - 1 - radius) / pageSize))
  const last = Math.min(
    totalPages - 1,
    Math.floor((rank - 1 + radius) / pageSize)
  )
  const pages: number[] = []
  for (let page = first; page <= last; page++) {
    pages.push(page)
  }
  return pages
}

export function groupPagesIntoBlocks<TRow>(
  pages: number[],
  rowsByPage: Map<number, TRow[]>
): LeaderboardBlock<TRow>[] {
  const blocks: LeaderboardBlock<TRow>[] = []
  for (const page of pages) {
    const rows = rowsByPage.get(page)
    if (!rows?.length) {
      continue
    }
    const last = blocks.at(-1)
    if (last && last.lastPage === page - 1) {
      last.lastPage = page
      last.rows = [...last.rows, ...rows]
    } else {
      blocks.push({ firstPage: page, lastPage: page, rows })
    }
  }
  return blocks
}

function findSelfRow<TRow>(
  blocks: LeaderboardBlock<TRow>[],
  selfPageRows: TRow[] | undefined,
  isSelfRow: ((row: TRow) => boolean) | undefined
): LeaderboardSelfRow<TRow> | undefined {
  if (!isSelfRow) {
    return undefined
  }
  for (const block of blocks) {
    const row = block.rows.find(item => isSelfRow(item))
    if (row) {
      return { row, inList: true }
    }
  }
  const row = selfPageRows?.find(item => isSelfRow(item))
  return row ? { row, inList: false } : undefined
}

/**
 * Page-based board that can hold several continuous ranges: the top of the
 * board plus a window around any rank. "Load more" extends the last range,
 * "load previous" extends a range upwards until it meets the one above.
 */
export function usePagedLeaderboard<TRow>({
  queryKey,
  fetchPage,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true,
  selfRank,
  isSelfRow
}: UsePagedLeaderboardOptions<TRow>) {
  const queryClient = useQueryClient()
  const keyHash = hashKey(queryKey)
  const [state, setState] = useState({ keyHash, pages: INITIAL_PAGES })
  const pages = state.keyHash === keyHash ? state.pages : INITIAL_PAGES

  const addPages = useCallback(
    (extra: number[]) =>
      setState(prev => {
        const base = prev.keyHash === keyHash ? prev.pages : INITIAL_PAGES
        const next = [...new Set([...base, ...extra])].sort((a, b) => a - b)
        return { keyHash, pages: next }
      }),
    [keyHash]
  )

  const pageQuery = useCallback(
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- the board key encodes what fetchPage reads
    (page: number) => ({
      queryKey: [...queryKey, pageSize, page],
      queryFn: () => fetchPage(page, pageSize),
      staleTime: STALE_TIME
    }),
    [keyHash, pageSize, fetchPage]
  )

  const results = useQueries({
    queries: pages.map(page => ({ ...pageQuery(page), enabled }))
  })

  const selfPage =
    selfRank && selfRank > 0 ? Math.floor((selfRank - 1) / pageSize) : -1
  const selfPageResult = useQuery({
    ...pageQuery(selfPage),
    enabled: enabled && selfPage >= 0
  })

  const firstResult = results[0]
  const total = firstResult?.data?.total ?? 0
  const totalPages = Math.ceil(total / pageSize)

  const rowsByPage = new Map<number, TRow[]>()
  pages.forEach((page, index) => {
    const rows = results[index]?.data?.rows
    if (rows) {
      rowsByPage.set(page, rows)
    }
  })
  const blocks = groupPagesIntoBlocks(pages, rowsByPage)

  const lastBlock = blocks.at(-1)
  const hasMore = Boolean(lastBlock && lastBlock.lastPage + 1 < totalPages)
  const isFetchingMore = results.some(
    (result, index) => index > 0 && result.isFetching && !result.data
  )

  const loadMore = useCallback(() => {
    if (lastBlock) {
      addPages([lastBlock.lastPage + 1])
    }
  }, [addPages, lastBlock])

  const loadPrevious = useCallback(
    (blockIndex: number) => {
      const block = blocks[blockIndex]
      if (block && block.firstPage > 0) {
        addPages([block.firstPage - 1])
      }
    },
    [addPages, blocks]
  )

  const revealRank = useCallback(
    async (rank: number, radius: number) => {
      const wanted = pagesAroundRank(
        rank,
        radius,
        pageSize,
        Math.max(totalPages, 1)
      )
      await Promise.all(
        wanted.map(page => queryClient.ensureQueryData(pageQuery(page)))
      )
      addPages(wanted)
    },
    [addPages, pageQuery, pageSize, queryClient, totalPages]
  )

  const selfRow = findSelfRow(blocks, selfPageResult.data?.rows, isSelfRow)

  return {
    blocks,
    total,
    isLoading: enabled && Boolean(firstResult?.isPending),
    isError: Boolean(firstResult?.isError),
    retry: () => firstResult?.refetch(),
    hasMore,
    isFetchingMore,
    loadMore,
    loadPrevious,
    revealRank,
    selfRow
  }
}
